import React, { useEffect } from 'react';
import Box from '@mui/material/Box';

function UserLicense() {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box component="main" className="bg-white min-h-screen font-sans text-gray-900">

            {/* Header / Title Section */}
            <div className="bg-slate-50 border-b border-gray-200 py-16">
                <div className="container mx-auto px-6 lg:px-40 max-w-7xl">
                    <h1 className="text-4xl lg:text-5xl font-bold text-secondary-500 mb-6 tracking-tight">
                        End-User License Agreement
                    </h1>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <p className="text-gray-600 font-medium mb-2">
                                Last Updated: 8 January 2026
                            </p>
                            <p className="text-xl text-gray-800 leading-relaxed max-w-3xl">
                                This End-User License Agreement ("Agreement") is a legal agreement between you and <span className="font-bold text-secondary-500">TTL Media Pvt. Ltd.</span> regarding your use of the Oye Creators mobile application and platform.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 lg:px-40 max-w-7xl py-12">
                <div className="prose prose-lg max-w-none text-gray-800">

                    {/* Section 1 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">1. Definitions</h2>
                        <div className="grid md:grid-cols-1 gap-6">
                            {[
                                { term: 'Application', def: 'means the mobile software applications (iOS and Android), updates, patches, and related software provided by Oye Creators.' },
                                { term: 'Platform', def: 'means the digital marketplace and related services operated by Oye Creators, including websites, dashboards, APIs, and support tools.' },
                                { term: 'Brands & Businesses', def: 'means entities that publish campaigns or tasks on the Platform.' },
                                { term: 'Creators', def: 'means independent content creators or service providers who apply for and execute campaigns upon approval.' },
                                { term: 'Content', def: 'means all user‑generated text, images, videos, audio, data, or other materials uploaded or created through the Platform.' }
                            ].map((item, index) => (
                                <div key={index} className="bg-white p-4 border-l-4 border-primary-100 rounded-r-lg hover:border-primary-500 transition-colors">
                                    <span className="font-bold text-gray-900 block mb-1">{item.term}</span>
                                    <span className="text-gray-800">{item.def}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">2. License Grant</h2>

                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">2.1 Scope</h3>
                            <p className="font-medium text-gray-800 leading-relaxed">
                                Oye Creators grants you a limited, non‑exclusive, non‑transferable, revocable license to install and use the Application on devices you own or control, solely for accessing the Platform in accordance with this Agreement.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">2.2 Restrictions</h3>
                            <p className="font-medium text-gray-800 leading-relaxed">
                                You shall not copy, modify, reverse engineer, distribute, sublicense, or use the Application for unlawful purposes or in violation of applicable laws or app‑store rules.
                            </p>
                        </div>
                    </section>

                    {/* Section 3 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">3. Updates & Support</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            We may provide automatic or mandatory updates to maintain security, functionality, or compliance. Failure to install mandatory updates may limit access to the Platform. Support is provided on a best‑effort basis.
                        </p>
                    </section>

                    {/* Section 4 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">4. Intellectual Property & Content</h2>

                        <div className="space-y-6">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">4.1 Application Ownership</h3>
                                <p className="font-medium text-gray-800 leading-relaxed">All rights in the Application remain the exclusive property of Oye Creators or its licensors.</p>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">4.2 User‑Generated Content License</h3>
                                <p className="font-medium text-gray-800 leading-relaxed mb-4">
                                    Creators retain ownership of their Content. By uploading Content, Creators grant Oye Creators and the relevant Brand a limited, non‑exclusive, royalty‑free license to use, display, reproduce, and distribute the Content solely for campaign execution, reporting, and platform operations, unless otherwise agreed in writing.
                                </p>
                                <p className="text-sm bg-gray-100 p-3 rounded text-gray-700 italic border border-gray-200">
                                    This license terminates when Content is removed or the campaign concludes, subject to legal retention requirements.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">4.3 Brand Materials</h3>
                                <p className="font-medium text-gray-800 leading-relaxed">Brands grant Oye Creators a limited license to use supplied trademarks or materials for campaign facilitation and platform display.</p>
                            </div>
                        </div>
                    </section>

                    {/* Section 5 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">5. User Responsibilities</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-gray-50 p-6 rounded border border-gray-200">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">5.1 Brands</h3>
                                <p className="font-medium text-gray-800">Brands are responsible for campaign legality, disclosures, and approvals.</p>
                            </div>
                            <div className="bg-gray-50 p-6 rounded border border-gray-200">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">5.2 Creators</h3>
                                <p className="font-medium text-gray-800 mb-3">Creators warrant that Content is original, lawful, and compliant with advertising disclosure laws (including ASCI). Sponsored content must be clearly labeled (e.g., #Ad).</p>
                                <p className="text-sm font-bold text-gray-600">Creators are independent contractors and are not employees, agents, or representatives of Oye Creators or Brands.</p>
                            </div>
                        </div>
                        <div className="mt-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">5.3 Platform Role</h3>
                            <p className="font-medium text-gray-800 leading-relaxed">Oye Creators acts solely as a technology intermediary and does not guarantee campaign selection, outcomes, or earnings.</p>
                        </div>
                    </section>

                    {/* Section 6 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">6. Privacy & Data Protection</h2>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
                            <p className="font-medium text-gray-800 leading-relaxed">
                                Use of the Application is governed by our Privacy Policy. Personal data is processed in accordance with the DPDP Act (India), GDPR (EU), and CCPA/CPRA (California), as applicable.
                            </p>
                        </div>
                    </section>

                    {/* Section 7 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">7. Eligibility</h2>
                        <div className="flex items-center space-x-4">
                            <span className="text-3xl">🔞</span>
                            <p className="font-bold text-gray-900 leading-relaxed text-lg">
                                The Application is intended only for users aged 18 years and above. Users under 18 are not permitted to access the Platform.
                            </p>
                        </div>
                    </section>

                    {/* Section 8 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">8. Disclaimers & Liability</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            The Application is provided "as is" and "as available." To the maximum extent permitted by law, Oye Creators disclaims all warranties and limits liability to fees paid by the user in the preceding 12 months.
                        </p>
                    </section>

                    {/* Section 9 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">9. Indemnification</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            You agree to indemnify and hold harmless Oye Creators from claims arising from your breach, unlawful conduct, or misuse of the Platform.
                        </p>
                    </section>

                    {/* Section 10 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">10. Termination</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            This Agreement remains effective until terminated by you (by uninstalling) or by Oye Creators for breach or discontinuation. Certain provisions survive termination.
                        </p>
                    </section>

                    {/* Section 11 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">11. Governing Law & Disputes</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            This Agreement is governed by Indian law. Disputes shall be resolved through arbitration in Pune, India.
                        </p>
                    </section>

                    {/* Section 12 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">12. App Store Precedence</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            If you access the Application through the Apple App Store or Google Play Store, and this Agreement conflicts with applicable store terms, the app store terms shall prevail to the extent required.
                        </p>
                    </section>

                    {/* Section 13 */}
                    <section className="mb-14">
                        <h2 className="text-2xl font-bold text-secondary-500 mb-6 pb-2 border-b border-gray-100 uppercase tracking-wide text-sm">13. Miscellaneous</h2>
                        <p className="font-medium text-gray-800 leading-relaxed">
                            This Agreement constitutes the entire agreement. If any provision is unenforceable, the remainder remains effective. Continued use after updates constitutes acceptance.
                        </p>
                    </section>

                </div>
            </div>
        </Box>
    );
}

export default UserLicense;
